import React, { useEffect, useState, useContext } from "react";
import {
  getPaymentMethods,
  getWalletBalance,
  getFamilyMembersByUserId,
  getCartByUserId,
  getUserAddressByUserId,
  placeOrder,
  deleteCart,
} from "../../services/Cart/PaymentMethod";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import { SlotContext } from "../../context/SlotContext";
import { useDispatch } from "react-redux";
import { setCart } from "../../Redux/CartSlice";
import { HiUserCircle } from "react-icons/hi";

export default function PaymentMethod({
  totalAmount,
  totalDiscount = 0,
  cartItems = [],
  userId,
  appliedCouponCode = "",
  appliedCouponId = 0,
  appliedCouponCategoryId = 0,
}) {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMembers, setShowMembers] = useState(false);

  const { selectedSlot } = useContext(SlotContext);
  const dispatch = useDispatch();

  // ✅ Dynamic userId – props se, nahi toh localStorage se
  const finalUserId =
    userId ||
    JSON.parse(localStorage.getItem("medarphaUser") || "{}")?.userId;

  console.log("═══════════════════════════════════════════════");
  console.log("👤 PAYMENT METHOD - USER ID:", finalUserId);
  console.log("═══════════════════════════════════════════════");

  const payableAmount =
    Number(totalAmount || 0) - Number(totalDiscount || 0);

  const requiresSlot = cartItems.some(
    (item) =>
      Number(item.categoryId) === 2 ||
      Number(item.categoryId) === 3
  );

  // Fetch payment methods
  useEffect(() => {
    const fetchMethods = async () => {
      try {
        const res = await getPaymentMethods();
        setMethods(res || []);
        console.log("✅ Payment Methods Loaded:", res);
      } catch (err) {
        console.error("❌ Error fetching payment methods:", err);
        setError("Failed to load payment methods");
      } finally {
        setLoading(false);
      }
    };
    fetchMethods();
  }, []);

  // Fetch wallet balance
  useEffect(() => {
    const fetchBalance = async () => {
      const methodName =
        selectedMethod?.paymentName ||
        selectedMethod?.paymenName;

      if (methodName === "Wallet") {
        try {
          const balance = await getWalletBalance(finalUserId);
          const safeBalance =
            balance?.currentBalance ??
            balance?.balance ??
            balance ??
            0;
          setWalletBalance(Number(safeBalance));
          console.log("💰 Wallet Balance:", safeBalance);
        } catch (err) {
          console.error("❌ Error fetching wallet balance:", err);
          setWalletBalance(0);
        }
      }
    };
    fetchBalance();
  }, [selectedMethod, finalUserId]);

  // Fetch family members
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        if (!finalUserId) return;

        const data = await getFamilyMembersByUserId(finalUserId);
        setFamilyMembers(data || []);
        console.log("👨‍👩‍👧‍👦 Family Members:", data);

        const defaultMember = data.find((m) => m.isDefault);
        setSelectedMember(defaultMember || data[0]);
      } catch (err) {
        console.log("❌ Family error:", err);
      }
    };
    fetchMembers();
  }, [finalUserId]);

  /* ================= PLACE ORDER ================= */
  /* ================= PLACE ORDER ================= */
const handlePlaceOrder = async () => {

  console.log("🛒 PLACE ORDER STARTED");


  const methodName =
    selectedMethod?.paymentName ||
    selectedMethod?.paymenName;

  // ✅ Validation 1: Payment Method
  if (!selectedMethod) {
    console.error("❌ No payment method selected");
    return toast.error("Please select payment method");
  }

  console.log("✅ Payment Method:", methodName, "ID:", selectedMethod?.id);

  // ✅ Validation 2: Wallet Balance
  if (methodName === "Wallet" && walletBalance < payableAmount) {
    console.error("❌ Insufficient wallet balance:", walletBalance, "Need:", payableAmount);
    return toast.error("Insufficient wallet balance");
  }

  // ✅ Validation 3: Slot
  if (requiresSlot) {
    const isSlotInvalid =
      !selectedSlot ||
      !selectedSlot.date ||
      !selectedSlot.time;

    if (isSlotInvalid) {
      console.error("❌ Slot not selected:", selectedSlot);
      return toast.error("Please select a delivery slot", {
        position: "bottom-center",
      });
    }
    console.log("✅ Slot Selected:", selectedSlot);
  }

  setIsProcessing(true);

  try {
    // ✅ Get Cart
    console.log("🛒 Fetching cart for user:", finalUserId);
    const cartRes = await getCartByUserId(finalUserId);
    console.log("📦 Cart Response:", cartRes);

    const cartData = cartRes?.data?.[0] || cartRes?.[0] || cartRes;

    if (!cartData) {
      console.error("❌ Cart is empty or invalid");
      return toast.error("Cart is empty");
    }

    console.log("🛒 Cart Data:", cartData);
    const cartId = cartData.cartId || cartData.id;
    console.log("🛒 Cart ID:", cartId);

    // ✅ Get Address
    console.log("📍 Fetching address for user:", finalUserId);
    const addressRes = await getUserAddressByUserId(finalUserId);
    console.log("📍 Address Response:", addressRes);

    const addressData = Array.isArray(addressRes) ? addressRes[0] : addressRes?.data?.[0] || addressRes;
    const selectedAddressId = addressData?.id;

    if (!selectedAddressId) {
      console.error("❌ No address found");
      return toast.error("No address found");
    }

    console.log("📍 Address ID:", selectedAddressId);

    // ✅ Build Final Payload
    const now = new Date();
    const formattedDate = now.toISOString();

    // ✅ FIX: Send time as "HH:mm:ss" string
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const timeString = `${hours}:${minutes}:${seconds}`;

    console.log("⏰ Order Time:", timeString);

    const payload = {
      userId: Number(finalUserId),
      cartId: Number(cartId),
      orderNumber: "ORD" + Date.now() + Math.floor(Math.random() * 1000),
      orderStatus: 0,
      paymentStatus: 0,
      paymentMethod: Number(selectedMethod?.id || 1),
      subTotal: Number(Number(totalAmount) + Number(totalDiscount)),
      discountAmount: Number(totalDiscount || 0),
      userAddressId: Number(selectedAddressId),
      finalAmount: Number(payableAmount),
      orderDate: formattedDate,
      orderTime: timeString, // ✅ Fixed: Send as string
      couponId: Number(appliedCouponId || 0),
      couponCategoryId: Number(appliedCouponCategoryId || 0),
    };

    console.log("═══════════════════════════════════════════════");
    console.log("📦 FINAL PAYLOAD BEING SENT:");
    console.log(JSON.stringify(payload, null, 2));
    console.log("═══════════════════════════════════════════════");

    // ✅ Place order
    console.log("🚀 Placing order...");
    const orderResponse = await placeOrder(payload);
    console.log("✅ Order Response:", orderResponse);

    toast.success("Order placed successfully");

    // ✅ Success Popup
    const oldPopup = document.getElementById("firebase-popup");
    if (oldPopup) oldPopup.remove();

    const popup = document.createElement("div");
    popup.id = "firebase-popup";
    popup.innerHTML = `
      <div style="
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.55);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 999999;
        backdrop-filter: blur(4px);
      ">
        <div style="
          width: 90%;
          max-width: 380px;
          background: linear-gradient(135deg,#16a34a,#22c55e);
          border-radius: 24px;
          padding: 30px 24px;
          text-align: center;
          color: white;
          animation: popupShow 0.35s ease;
          box-shadow: 0 10px 40px rgba(0,0,0,0.3);
          position: relative;
          overflow: hidden;
        ">
          <div style="
            width: 80px;
            height: 80px;
            background: rgba(255,255,255,0.18);
            border-radius: 50%;
            display:flex;
            align-items:center;
            justify-content:center;
            margin: 0 auto 18px;
            font-size: 42px;
          ">🎉</div>
          <h2 style="margin:0; font-size:26px; font-weight:700;">Order Confirmed</h2>
          <p style="margin-top:14px; font-size:15px; line-height:1.6; color:#f0fdf4;">
            Your order has been placed successfully.
          </p>
          <button id="close-popup-btn" style="
            margin-top:22px;
            background:white;
            color:#16a34a;
            border:none;
            padding:12px 28px;
            border-radius:14px;
            font-size:15px;
            font-weight:600;
            cursor:pointer;
            box-shadow:0 4px 14px rgba(0,0,0,0.15);
          ">Continue Shopping</button>
        </div>
      </div>
      <style>
        @keyframes popupShow {
          from { transform: scale(.7); opacity:0; }
          to { transform: scale(1); opacity:1; }
        }
      </style>
    `;

    document.body.appendChild(popup);

    setTimeout(() => {
      const closeBtn = document.getElementById("close-popup-btn");
      if (closeBtn) closeBtn.onclick = () => popup.remove();
    }, 100);

    setTimeout(() => popup.remove(), 8000);

    // ✅ Clear cart
    console.log("🗑️ Clearing cart...");
    dispatch(setCart([]));
    await deleteCart(cartId);
    console.log("✅ Cart cleared successfully");

  } catch (err) {
    console.error("═══════════════════════════════════════════════");
    console.error("❌ ORDER FAILED");
    console.error("═══════════════════════════════════════════════");
    console.error("Error:", err);
    console.error("Error Response:", err?.response);
    console.error("Error Data:", err?.response?.data);
    console.error("Error Message:", err?.message);
    
    if (err?.response?.data?.errors) {
      console.error("Detailed Errors:", JSON.stringify(err.response.data.errors, null, 2));
    }

    const errorMsg =
      err?.response?.data?.message ||
      err?.response?.data?.title ||
      err?.message ||
      "Order failed";
    
    toast.error(errorMsg);
  } finally {
    setIsProcessing(false);
    console.log("═══════════════════════════════════════════════");
    console.log("🏁 PLACE ORDER COMPLETED");
    console.log("═══════════════════════════════════════════════");
  }
};

  return (
    <div className="bg-white p-4 rounded-xl shadow mt-4" onClick={(e) => e.stopPropagation()}>
      <ToastContainer />

      {/* Family Member */}
      {selectedMember && (
        <div className="border rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <HiUserCircle size={40} className="text-gray-400" />
              <div>
                <p className="font-semibold text-sm">
                  {selectedMember.firstName} {selectedMember.lastName}
                </p>
                <p className="text-xs text-gray-500">{selectedMember.relationName}</p>
              </div>
            </div>
            <button
              onClick={() => setShowMembers(!showMembers)}
              className="text-red-500 border px-3 py-1 rounded-lg text-sm bg-white hover:bg-gray-50"
            >
              Change
            </button>
          </div>

          {showMembers && (
            <div className="mt-3 space-y-2 max-h-[150px] overflow-y-auto">
              {familyMembers.map((m) => (
                <div
                  key={m.id}
                  onClick={() => {
                    setSelectedMember(m);
                    setShowMembers(false);
                  }}
                  className="p-2 border rounded-lg cursor-pointer hover:bg-gray-50 text-sm"
                >
                  {m.firstName} {m.lastName} ({m.relationName || "Self"})
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="bg-gray-50 p-3 rounded-lg mb-4 text-sm space-y-1.5">
        <h4 className="font-bold text-gray-700 pb-1 border-b mb-2">Payment Summary</h4>
        <div className="flex justify-between text-gray-600">
          <span>Subtotal:</span>
          <span>₹{(Number(totalAmount) - Number(totalDiscount)).toFixed(2)}</span>
        </div>
        {Number(totalDiscount) > 0 && (
          <div className="flex justify-between text-green-600 font-medium">
            <span>Coupon Discount:</span>
            <span>- ₹{Number(totalDiscount).toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between font-bold text-base text-gray-800 pt-1.5 border-t border-dashed">
          <span>Total Payable:</span>
          <span className="text-green-600">₹{payableAmount.toFixed(2)}</span>
        </div>
      </div>

      {/* Selected Slot */}
      {selectedSlot && (
        <div className="mt-3 mb-3 border rounded-lg p-3 shadow-sm bg-white text-sm">
          <h5 className="font-bold text-gray-700 mb-2">Selected Slot</h5>
          <div className="flex items-center gap-2 mb-2">
            <span className="font-semibold text-gray-500">Date:</span>
            <span className="bg-gray-100 text-gray-800 border px-2.5 py-1 text-xs rounded-full font-medium">
              {selectedSlot?.date ? format(new Date(selectedSlot.date), "dd MMM yyyy") : "N/A"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-500">Time:</span>
            <span className="bg-red-50 text-red-600 border border-red-200 px-2.5 py-1 text-xs rounded-full font-medium">
              {selectedSlot?.time || "N/A"}
            </span>
          </div>
        </div>
      )}

      {/* Payment Methods */}
      <div className="mb-3">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">
          Payment Method
        </label>
        {!loading && methods.length > 0 ? (
          <select
            className="w-full border p-2.5 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={selectedMethod?.id || ""}
            onChange={(e) =>
              setSelectedMethod(methods.find((m) => m.id === Number(e.target.value)))
            }
          >
            <option value="">Select Payment Method</option>
            {methods.map((m) => (
              <option key={m.id} value={m.id}>
                {m.paymentName || m.paymenName}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-xs text-gray-400 animate-pulse">Loading methods...</p>
        )}
      </div>

      {/* Wallet Display */}
      {(selectedMethod?.paymentName === "Wallet" || selectedMethod?.paymenName === "Wallet") && (
        <div className="bg-blue-50 border border-blue-200 p-2.5 rounded-lg mb-3 flex justify-between items-center text-sm">
          <span className="text-blue-700 font-medium">Available Wallet Balance:</span>
          <span className="font-bold text-blue-800">₹{walletBalance}</span>
        </div>
      )}

      {error && <p className="text-red-500 text-xs font-medium mb-2">⚠️ {error}</p>}

      {/* Place Order Button */}
      <button
        onClick={handlePlaceOrder}
        disabled={isProcessing}
        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 rounded-lg transition disabled:opacity-50 shadow-md"
      >
        {isProcessing ? "Placing Order..." : `Pay ₹${payableAmount.toFixed(2)}`}
      </button>
    </div>
  );
}